import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ContentManager from "./components/ContentManager";
import ReviewsModeration from "./components/ReviewsModeration";
import SiteStats from "./components/SiteStats";
import MembershipManager from "./components/MembershipManager";
import AnnouncementsManager from "./components/AnnouncementsManager";
import StatsChart from "./components/StatsChart";
import AccommodationSection from "./components/AccommodationSection";
import SeoStatus from "./components/SeoStatus";
import SubmissionHistory from "./components/SubmissionHistory";

interface PendingRestaurant {
  id: string;
  restaurantName: string;
  ownerName: string;
  email: string;
  phone: string;
  city: string;
  district: string;
  address: string;
  cuisine: string;
  priceRange: string;
  shortDesc: string;
  description: string;
  mustTry: string[];
  website: string;
  instagram: string;
  openingHours: string;
  status: "pending" | "approved" | "rejected";
  appliedAt: string;
  image: string;
  images: string[];
  tags: string[];
  featured: boolean;
  rating: number;
  notified?: boolean;
  adminNote?: string;
  lat?: number;
  lng?: number;
}

const DEFAULT_ADMIN_PASSWORD = "Rehber@2026!";
const ADMIN_EMAIL = "admin@turkiyeseyahat.com";

const getAdminPassword = () => localStorage.getItem("adminPassword") || DEFAULT_ADMIN_PASSWORD;

const AdminPage = () => {
  const navigate = useNavigate();
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [applications, setApplications] = useState<PendingRestaurant[]>([]);
  const [filter, setFilter] = useState<"all" | "pending" | "approved" | "rejected">("pending");
  const [selectedApp, setSelectedApp] = useState<PendingRestaurant | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: "success" | "error" | "info" } | null>(null);
  const [notifPanelOpen, setNotifPanelOpen] = useState(false);
  const [seenIds, setSeenIds] = useState<string[]>([]);
  const [sendingEmail, setSendingEmail] = useState<"approve" | "reject" | null>(null);
  const [showStats, setShowStats] = useState(false);
  const [restNoteEdit, setRestNoteEdit] = useState("");
  const [showRestNoteEdit, setShowRestNoteEdit] = useState(false);
  const [activeSection, setActiveSection] = useState<"applications" | "accommodations" | "membership" | "content" | "reviews" | "stats" | "announcements" | "seo" | "history" | "settings">("applications");
  const [quickAddOpen, setQuickAddOpen] = useState(false);

  // Restoran toplu işlem state
  const [selectedRestIds, setSelectedRestIds] = useState<Set<string>>(new Set());
  const [restBulkProcessing, setRestBulkProcessing] = useState(false);

  // Restoran arama & filtre
  const [restSearch, setRestSearch] = useState("");
  const [restCityFilter, setRestCityFilter] = useState("all");
  const [restCuisineFilter, setRestCuisineFilter] = useState("all");
  const [restPriceFilter, setRestPriceFilter] = useState("all");

  // Password change state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pwChangeError, setPwChangeError] = useState("");
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);

  // Notification email state
  const [notifEmail, setNotifEmail] = useState(() => localStorage.getItem("adminNotifEmail") || "");
  const [notifEmailSaved, setNotifEmailSaved] = useState(false);

  useEffect(() => {
    const auth = sessionStorage.getItem("adminAuth");
    if (auth === "true") setAuthenticated(true);
    const seen = JSON.parse(localStorage.getItem("adminSeenIds") || "[]");
    setSeenIds(seen);
  }, []);

  useEffect(() => {
    if (authenticated) {
      loadApplications();
    }
  }, [authenticated]);

  const loadApplications = () => {
    const pending = JSON.parse(localStorage.getItem("pendingRestaurants") || "[]");
    setApplications(pending);
  };

  const showToast = (msg: string, type: "success" | "error" | "info") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === getAdminPassword()) {
      sessionStorage.setItem("adminAuth", "true");
      setAuthenticated(true);
      setPasswordError("");
    } else {
      setPasswordError("Hatalı şifre. Tekrar deneyin.");
    }
  };

  const handlePasswordChange = (e: React.FormEvent) => {
    e.preventDefault();
    setPwChangeError("");
    if (currentPassword !== getAdminPassword()) {
      setPwChangeError("Mevcut şifre hatalı.");
      return;
    }
    if (newPassword.length < 6) {
      setPwChangeError("Yeni şifre en az 6 karakter olmalıdır.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwChangeError("Yeni şifreler eşleşmiyor.");
      return;
    }
    localStorage.setItem("adminPassword", newPassword);
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    showToast("Şifre başarıyla değiştirildi!", "success");
  };

  const handleSaveNotifEmail = (e: React.FormEvent) => {
    e.preventDefault();
    localStorage.setItem("adminNotifEmail", notifEmail);
    setNotifEmailSaved(true);
    showToast("Bildirim e-postası kaydedildi!", "success");
    setTimeout(() => setNotifEmailSaved(false), 3000);
  };

  const markAllSeen = () => {
    const allIds = applications.filter((a) => a.status === "pending").map((a) => a.id);
    const merged = Array.from(new Set([...seenIds, ...allIds]));
    setSeenIds(merged);
    localStorage.setItem("adminSeenIds", JSON.stringify(merged));
    setNotifPanelOpen(false);
  };

  const unseenPending = applications.filter(
    (a) => a.status === "pending" && !seenIds.includes(a.id)
  );

  const sendEmailNotification = (app: PendingRestaurant, type: "approve" | "reject") => {
    setSendingEmail(type);
    setTimeout(() => {
      const pending: PendingRestaurant[] = JSON.parse(localStorage.getItem("pendingRestaurants") || "[]");
      const updated = pending.map((p) => p.id === app.id ? { ...p, notified: true } : p);
      localStorage.setItem("pendingRestaurants", JSON.stringify(updated));
      setApplications(updated);
      setSelectedApp((prev) => prev ? { ...prev, notified: true } : prev);
      setSendingEmail(null);

      const subject = type === "approve"
        ? `Tebrikler! "${app.restaurantName}" Başvurunuz Onaylandı`
        : `"${app.restaurantName}" Başvurunuz Hakkında Bilgi`;
      const body = type === "approve"
        ? `Sayın ${app.ownerName},\n\nRestoranınız "${app.restaurantName}" onaylandı ve sitemizde yayınlandı.\n\nİyi çalışmalar,\nTürkiye Seyahat Rehberi Ekibi`
        : `Sayın ${app.ownerName},\n\nRestoranınız "${app.restaurantName}" için yaptığınız başvuruyu inceledik. Maalesef şu an için başvurunuzu onaylayamıyoruz.\n\nİyi çalışmalar,\nTürkiye Seyahat Rehberi Ekibi`;

      const mailtoLink = `mailto:${app.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}&cc=${encodeURIComponent(ADMIN_EMAIL)}`;
      window.open(mailtoLink, "_blank");
      showToast(`E-posta taslağı hazırlandı — ${app.email} adresine gönderilecek`, "info");
    }, 800);
  };

  const handleApprove = (app: PendingRestaurant) => {
    const pending: PendingRestaurant[] = JSON.parse(localStorage.getItem("pendingRestaurants") || "[]");
    const updated = pending.map((p) => p.id === app.id ? { ...p, status: "approved" as const } : p);
    localStorage.setItem("pendingRestaurants", JSON.stringify(updated));

    const approved = JSON.parse(localStorage.getItem("approvedRestaurants") || "[]");
    const newRestaurant = {
      id: app.restaurantName.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "") + "-" + Date.now(),
      name: app.restaurantName,
      city: app.city,
      district: app.district,
      cuisine: app.cuisine,
      shortDesc: app.shortDesc,
      description: app.description,
      image: app.image,
      images: app.images,
      tags: app.tags,
      rating: 4.5,
      priceRange: app.priceRange,
      mustTry: app.mustTry,
      featured: false,
      address: app.address,
      phone: app.phone,
      website: app.website,
      instagram: app.instagram,
      openingHours: app.openingHours,
      ownerSubmitted: true,
      approvedAt: new Date().toISOString(),
      lat: app.lat,
      lng: app.lng,
    };
    approved.push(newRestaurant);
    localStorage.setItem("approvedRestaurants", JSON.stringify(approved));

    setApplications(updated);
    setSelectedApp((prev) => prev ? { ...prev, status: "approved" } : prev);
    showToast(`"${app.restaurantName}" onaylandı ve listeye eklendi!`, "success");

    setTimeout(() => sendEmailNotification({ ...app, status: "approved" }, "approve"), 500);
  };

  const handleReject = (app: PendingRestaurant) => {
    const pending: PendingRestaurant[] = JSON.parse(localStorage.getItem("pendingRestaurants") || "[]");
    const updated = pending.map((p) => p.id === app.id ? { ...p, status: "rejected" as const } : p);
    localStorage.setItem("pendingRestaurants", JSON.stringify(updated));
    setApplications(updated);
    setSelectedApp((prev) => prev ? { ...prev, status: "rejected" } : prev);
    showToast(`"${app.restaurantName}" reddedildi.`, "error");

    setTimeout(() => sendEmailNotification({ ...app, status: "rejected" }, "reject"), 500);
  };

  const handleDelete = (id: string) => {
    const pending: PendingRestaurant[] = JSON.parse(localStorage.getItem("pendingRestaurants") || "[]");
    const updated = pending.filter((p) => p.id !== id);
    localStorage.setItem("pendingRestaurants", JSON.stringify(updated));
    setApplications(updated);
    setSelectedApp(null);
    showToast("Başvuru silindi.", "error");
  };

  const filtered = applications
    .filter((a) => filter === "all" || a.status === filter)
    .filter((a) => {
      if (!restSearch.trim()) return true;
      const q = restSearch.toLowerCase();
      return (
        a.restaurantName.toLowerCase().includes(q) ||
        a.ownerName.toLowerCase().includes(q) ||
        a.city.toLowerCase().includes(q) ||
        a.email.toLowerCase().includes(q)
      );
    })
    .filter((a) => restCityFilter === "all" || a.city === restCityFilter)
    .filter((a) => restCuisineFilter === "all" || a.cuisine === restCuisineFilter)
    .filter((a) => restPriceFilter === "all" || a.priceRange === restPriceFilter);

  const restCities = Array.from(new Set(applications.map((a) => a.city))).filter(Boolean).sort();
  const restCuisines = Array.from(new Set(applications.map((a) => a.cuisine))).filter(Boolean).sort();
  const restPrices = Array.from(new Set(applications.map((a) => a.priceRange))).filter(Boolean).sort();
  const pendingCount = applications.filter((a) => a.status === "pending").length;
  const approvedCount = applications.filter((a) => a.status === "approved").length;
  const rejectedCount = applications.filter((a) => a.status === "rejected").length;

  const statusBadge = (status: string) => {
    if (status === "pending") return <span className="bg-amber-100 text-amber-700 text-xs px-2 py-0.5 rounded-full font-medium">Bekliyor</span>;
    if (status === "approved") return <span className="bg-emerald-100 text-emerald-700 text-xs px-2 py-0.5 rounded-full font-medium">Onaylandı</span>;
    return <span className="bg-rose-100 text-rose-700 text-xs px-2 py-0.5 rounded-full font-medium">Reddedildi</span>;
  };

  const toastColors: Record<string, string> = {
    success: "bg-emerald-500 text-white",
    error: "bg-rose-500 text-white",
    info: "bg-sky-500 text-white",
  };
  const toastIcons: Record<string, string> = {
    success: "ri-checkbox-circle-line",
    error: "ri-close-circle-line",
    info: "ri-mail-send-line",
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 bg-stone-900 rounded-2xl">
              <i className="ri-shield-keyhole-line text-amber-400 text-2xl"></i>
            </div>
            <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
              Admin Paneli
            </h1>
            <p className="text-stone-400 text-sm mt-1">Restoran başvurularını yönetin</p>
          </div>
          <form onSubmit={handleLogin} className="bg-white rounded-2xl border border-stone-100 p-6">
            <label className="block text-xs font-semibold text-stone-600 mb-1.5">Şifre</label>
            <input
              type="password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setPasswordError(""); }}
              placeholder="Admin şifresi"
              className={`w-full border rounded-lg px-3 py-2.5 text-sm text-stone-800 outline-none transition-colors mb-1
                ${passwordError ? "border-rose-400 bg-rose-50" : "border-stone-200 focus:border-amber-400"}`}
            />
            {passwordError && <p className="text-rose-500 text-xs mb-3">{passwordError}</p>}
            <button
              type="submit"
              className="w-full mt-4 py-2.5 bg-stone-900 text-white rounded-lg text-sm font-medium hover:bg-stone-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              Giriş Yap
            </button>
            <p className="text-center text-xs text-stone-400 mt-4">
              Admin şifresi size özel olarak oluşturulmuştur.
            </p>
          </form>
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-1 text-stone-400 text-xs mx-auto mt-4 hover:text-stone-600 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i> Ana sayfaya dön
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />

      {/* Toast */}
      {toast && (
        <div className={`fixed top-20 right-4 z-50 px-4 py-3 rounded-xl text-sm font-medium flex items-center gap-2 transition-all ${toastColors[toast.type]}`}>
          <i className={toastIcons[toast.type]}></i>
          {toast.msg}
        </div>
      )}

      {/* Quick Add Overlay */}
      {quickAddOpen && (
        <div className="fixed inset-0 z-40" onClick={() => setQuickAddOpen(false)}></div>
      )}

      {/* Notification Panel Overlay */}
      {notifPanelOpen && (
        <div className="fixed inset-0 z-40" onClick={() => setNotifPanelOpen(false)}>
          <div
            className="absolute top-20 right-4 w-80 bg-white rounded-2xl border border-stone-100 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-stone-100">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-notification-3-line text-stone-700 text-sm"></i>
                </div>
                <span className="text-sm font-semibold text-stone-800">Bildirimler</span>
                {unseenPending.length > 0 && (
                  <span className="bg-amber-500 text-white text-xs px-1.5 py-0.5 rounded-full">{unseenPending.length}</span>
                )}
              </div>
              {unseenPending.length > 0 && (
                <button onClick={markAllSeen} className="text-xs text-amber-600 hover:text-amber-700 cursor-pointer whitespace-nowrap">
                  Tümünü okundu işaretle
                </button>
              )}
            </div>
            <div className="max-h-72 overflow-y-auto">
              {unseenPending.length === 0 ? (
                <div className="py-8 text-center">
                  <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2">
                    <i className="ri-check-double-line text-emerald-400 text-2xl"></i>
                  </div>
                  <p className="text-stone-400 text-xs">Tüm bildirimler okundu</p>
                </div>
              ) : (
                unseenPending.map((app) => (
                  <button
                    key={app.id}
                    onClick={() => {
                      setSelectedApp(app);
                      setFilter("pending");
                      const merged = Array.from(new Set([...seenIds, app.id]));
                      setSeenIds(merged);
                      localStorage.setItem("adminSeenIds", JSON.stringify(merged));
                      setNotifPanelOpen(false);
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-amber-50 transition-colors border-b border-stone-50 last:border-0 cursor-pointer"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-amber-500 mt-1.5 flex-shrink-0"></div>
                      <div>
                        <p className="text-sm font-semibold text-stone-800">{app.restaurantName}</p>
                        <p className="text-xs text-stone-400 mt-0.5">{app.city} · {app.cuisine}</p>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
            {applications.filter((a) => a.status === "pending").length > 0 && (
              <div className="px-4 py-3 border-t border-stone-100">
                <button
                  onClick={() => { setFilter("pending"); setNotifPanelOpen(false); }}
                  className="w-full text-center text-xs text-amber-600 font-medium hover:text-amber-700 cursor-pointer"
                >
                  Tüm bekleyen başvuruları gör ({pendingCount})
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Header */}
      <section className="pt-24 pb-8 bg-stone-900">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <span className="text-amber-400 text-xs font-semibold uppercase tracking-widest">Admin Paneli</span>
              <h1 className="text-2xl md:text-3xl font-bold text-white mt-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                Yönetim Merkezi
              </h1>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center bg-stone-700 rounded-full p-1 gap-1 flex-wrap">
                {([
                  { key: "applications", icon: "ri-store-2-line", label: "Başvurular" },
                  { key: "accommodations", icon: "ri-hotel-line", label: "Konaklama" },
                  { key: "membership", icon: "ri-vip-crown-line", label: "Üyelik" },
                  { key: "content", icon: "ri-edit-2-line", label: "İçerik" },
                  { key: "reviews", icon: "ri-chat-3-line", label: "Yorumlar" },
                  { key: "stats", icon: "ri-pie-chart-line", label: "İstatistik" },
                  { key: "announcements", icon: "ri-megaphone-line", label: "Duyurular" },
                  { key: "seo", icon: "ri-search-eye-line", label: "SEO" },
                  { key: "history", icon: "ri-history-line", label: "Geçmiş" },
                  { key: "settings", icon: "ri-settings-3-line", label: "Ayarlar" },
                ] as const).map((item) => (
                  <button
                    key={item.key}
                    onClick={() => setActiveSection(item.key)}
                    className={`text-xs px-3 py-1.5 rounded-full font-medium transition-all cursor-pointer whitespace-nowrap
                      ${activeSection === item.key ? "bg-amber-500 text-white" : "text-stone-300 hover:text-white"}`}
                  >
                    <i className={`${item.icon} mr-1`}></i>{item.label}
                  </button>
                ))}
              </div>
              {activeSection === "applications" && (
                <button
                  onClick={() => setShowStats((v) => !v)}
                  className={`flex items-center gap-1.5 text-xs px-3 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap
                    ${showStats ? "bg-amber-500 text-white" : "bg-stone-700 text-stone-300 hover:bg-stone-600"}`}
                >
                  <i className="ri-bar-chart-2-line"></i> İstatistikler
                </button>
              )}
              {/* Hızlı İçerik Ekle */}
              <div className="relative">
                <button
                  onClick={() => setQuickAddOpen((v) => !v)}
                  className="flex items-center gap-1.5 text-xs px-3 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-full transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line"></i> İçerik Ekle
                </button>
                {quickAddOpen && (
                  <div className="absolute top-10 right-0 z-50 w-52 bg-white rounded-2xl border border-stone-100 overflow-hidden">
                    <div className="px-4 py-3 border-b border-stone-100">
                      <p className="text-xs font-semibold text-stone-600">Hızlı İçerik Ekle</p>
                    </div>
                    {[
                      { icon: "ri-article-line", label: "Yeni Makale", section: "content" as const, color: "text-amber-500" },
                      { icon: "ri-map-pin-line", label: "Yeni Yer", section: "content" as const, color: "text-emerald-500" },
                      { icon: "ri-building-4-line", label: "Yeni Şehir / Bölge", section: "content" as const, color: "text-stone-500" },
                      { icon: "ri-megaphone-line", label: "Yeni Duyuru", section: "announcements" as const, color: "text-rose-500" },
                    ].map((item) => (
                      <button
                        key={item.label}
                        onClick={() => { setActiveSection(item.section); setQuickAddOpen(false); }}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-stone-50 transition-colors cursor-pointer text-left border-b border-stone-50 last:border-0"
                      >
                        <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-stone-100">
                          <i className={`${item.icon} ${item.color} text-sm`}></i>
                        </div>
                        <span className="text-sm text-stone-700 font-medium">{item.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button
                onClick={() => setNotifPanelOpen((v) => !v)}
                className="relative flex items-center justify-center w-9 h-9 bg-stone-700 hover:bg-stone-600 text-stone-300 rounded-full transition-colors cursor-pointer"
              >
                <i className="ri-notification-3-line text-base"></i>
                {unseenPending.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-amber-500 text-white text-xs rounded-full flex items-center justify-center font-bold leading-none">
                    {unseenPending.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => { sessionStorage.removeItem("adminAuth"); setAuthenticated(false); }}
                className="flex items-center gap-1.5 text-xs px-3 py-2 bg-stone-700 text-stone-300 rounded-full hover:bg-stone-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-logout-box-line"></i> Çıkış
              </button>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 mt-6">
            <div className="bg-stone-800 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-amber-400">{pendingCount}</p>
              <p className="text-stone-400 text-xs mt-1">Bekleyen</p>
            </div>
            <div className="bg-stone-800 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-emerald-400">{approvedCount}</p>
              <p className="text-stone-400 text-xs mt-1">Onaylanan</p>
            </div>
            <div className="bg-stone-800 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-rose-400">{rejectedCount}</p>
              <p className="text-stone-400 text-xs mt-1">Reddedilen</p>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">

        {activeSection === "accommodations" && (
          <AccommodationSection showToast={showToast} />
        )}

        {activeSection === "membership" && <MembershipManager />}
        {activeSection === "content" && <ContentManager />}
        {activeSection === "reviews" && <ReviewsModeration />}
        {activeSection === "stats" && <SiteStats />}
        {activeSection === "announcements" && <AnnouncementsManager showToast={showToast} />}
        {activeSection === "seo" && <SeoStatus />}
        {activeSection === "history" && <SubmissionHistory />}

        {activeSection === "settings" && (
          <div className="max-w-lg space-y-0">
            <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden">
              <div className="px-6 py-5 border-b border-stone-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-stone-100 rounded-xl">
                    <i className="ri-lock-password-line text-stone-600 text-lg"></i>
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                      Şifre Değiştir
                    </h2>
                    <p className="text-xs text-stone-400 mt-0.5">Admin giriş şifrenizi güncelleyin</p>
                  </div>
                </div>
              </div>
              <form onSubmit={handlePasswordChange} className="p-6 space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-stone-600 mb-1.5">Mevcut Şifre</label>
                  <div className="relative">
                    <input
                      type={showCurrentPw ? "text" : "password"}
                      value={currentPassword}
                      onChange={(e) => { setCurrentPassword(e.target.value); setPwChangeError(""); }}
                      placeholder="Mevcut şifrenizi girin"
                      className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-800 outline-none focus:border-amber-400 transition-colors pr-10"
                    />
                    <button type="button" onClick={() => setShowCurrentPw((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-stone-400 hover:text-stone-600 cursor-pointer">
                      <i className={showCurrentPw ? "ri-eye-off-line" : "ri-eye-line"}></i>
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-stone-600 mb-1.5">Yeni Şifre</label>
                  <div className="relative">
                    <input
                      type={showNewPw ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => { setNewPassword(e.target.value); setPwChangeError(""); }}
                      placeholder="En az 6 karakter"
                      className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-800 outline-none focus:border-amber-400 transition-colors pr-10"
                    />
                    <button type="button" onClick={() => setShowNewPw((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-stone-400 hover:text-stone-600 cursor-pointer">
                      <i className={showNewPw ? "ri-eye-off-line" : "ri-eye-line"}></i>
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-stone-600 mb-1.5">Yeni Şifre (Tekrar)</label>
                  <div className="relative">
                    <input
                      type={showConfirmPw ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => { setConfirmPassword(e.target.value); setPwChangeError(""); }}
                      placeholder="Yeni şifrenizi tekrar girin"
                      className={`w-full border rounded-lg px-3 py-2.5 text-sm text-stone-800 outline-none transition-colors pr-10
                        ${confirmPassword && confirmPassword !== newPassword ? "border-rose-400 bg-rose-50" : confirmPassword && confirmPassword === newPassword ? "border-emerald-400 bg-emerald-50" : "border-stone-200 focus:border-amber-400"}`}
                    />
                    <button type="button" onClick={() => setShowConfirmPw((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-stone-400 hover:text-stone-600 cursor-pointer">
                      <i className={showConfirmPw ? "ri-eye-off-line" : "ri-eye-line"}></i>
                    </button>
                  </div>
                </div>
                {pwChangeError && (
                  <div className="flex items-center gap-2 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2.5">
                    <i className="ri-error-warning-line text-rose-500 text-sm"></i>
                    <p className="text-rose-600 text-xs">{pwChangeError}</p>
                  </div>
                )}
                <button type="submit" className="w-full py-2.5 bg-stone-900 text-white rounded-lg text-sm font-medium hover:bg-stone-800 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-save-line mr-1.5"></i>Şifreyi Kaydet
                </button>
              </form>
            </div>

            <div className="mt-4 bg-stone-100 rounded-xl p-4">
              <div className="flex items-start gap-2">
                <div className="w-4 h-4 flex items-center justify-center mt-0.5">
                  <i className="ri-information-line text-stone-500 text-sm"></i>
                </div>
                <p className="text-xs text-stone-500 leading-relaxed">
                  Şifreniz tarayıcınızda güvenli şekilde saklanır. Tarayıcı verilerini temizlerseniz şifre varsayılan değere döner: <strong>Rehber@2026!</strong>
                </p>
              </div>
            </div>

            <div className="mt-6 bg-white rounded-2xl border border-stone-100 overflow-hidden">
              <div className="px-6 py-5 border-b border-stone-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-xl">
                    <i className="ri-mail-settings-line text-amber-600 text-lg"></i>
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                      Bildirim E-postası
                    </h2>
                    <p className="text-xs text-stone-400 mt-0.5">Yeni başvurularda (restoran, konaklama, Gold) bu adrese otomatik bildirim gönderilir</p>
                  </div>
                </div>
              </div>
              <form onSubmit={handleSaveNotifEmail} className="p-6 space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-stone-600 mb-1.5">Admin E-posta Adresi</label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                      <i className="ri-mail-line text-stone-400 text-sm"></i>
                    </div>
                    <input
                      type="email"
                      value={notifEmail}
                      onChange={(e) => { setNotifEmail(e.target.value); setNotifEmailSaved(false); }}
                      placeholder="admin@geziturkiyem.com"
                      className="w-full border border-stone-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-stone-800 outline-none focus:border-amber-400 transition-colors"
                    />
                  </div>
                  <p className="text-xs text-stone-400 mt-1.5">
                    Girilmezse varsayılan olarak <strong>admin@geziturkiyem.com</strong> kullanılır
                  </p>
                </div>

                {/* Bildirim türleri */}
                <div className="bg-stone-50 rounded-xl p-4 space-y-2">
                  <p className="text-xs font-semibold text-stone-600 mb-2">Bildirim Gönderilecek Durumlar:</p>
                  {[
                    { icon: "ri-store-2-line", label: "Yeni restoran başvurusu", color: "text-amber-500" },
                    { icon: "ri-hotel-line", label: "Yeni konaklama başvurusu", color: "text-stone-500" },
                    { icon: "ri-vip-crown-line", label: "Yeni Gold üyelik talebi", color: "text-amber-500" },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center gap-2">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className={`${item.icon} ${item.color} text-sm`}></i>
                      </div>
                      <span className="text-xs text-stone-600">{item.label}</span>
                      <div className="ml-auto w-4 h-4 flex items-center justify-center">
                        <i className="ri-checkbox-circle-fill text-emerald-500 text-sm"></i>
                      </div>
                    </div>
                  ))}
                </div>

                <button
                  type="submit"
                  className="w-full py-2.5 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  {notifEmailSaved
                    ? <><i className="ri-checkbox-circle-line"></i> Kaydedildi!</>
                    : <><i className="ri-save-line"></i> E-postayı Kaydet</>}
                </button>
              </form>
            </div>
          </div>
        )}

        {activeSection === "applications" && (
          <>
            {showStats && <StatsChart applications={applications} />}

            {/* Arama + Filtreler */}
            <div className="bg-white rounded-xl border border-stone-100 p-4 mb-4 space-y-3">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex-1 min-w-48 flex items-center bg-stone-50 border border-stone-200 rounded-lg px-3 gap-2 focus-within:border-amber-400 transition-colors">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    <i className="ri-search-line text-stone-400 text-sm"></i>
                  </div>
                  <input
                    type="text"
                    value={restSearch}
                    onChange={(e) => setRestSearch(e.target.value)}
                    placeholder="Restoran, sahip, şehir veya e-posta ara..."
                    className="flex-1 bg-transparent text-sm text-stone-700 outline-none py-2"
                  />
                  {restSearch && (
                    <button onClick={() => setRestSearch("")} className="w-4 h-4 flex items-center justify-center text-stone-400 hover:text-stone-600 cursor-pointer">
                      <i className="ri-close-line text-sm"></i>
                    </button>
                  )}
                </div>
                {(restSearch || restCityFilter !== "all" || restCuisineFilter !== "all" || restPriceFilter !== "all") && (
                  <button
                    onClick={() => { setRestSearch(""); setRestCityFilter("all"); setRestCuisineFilter("all"); setRestPriceFilter("all"); }}
                    className="flex items-center gap-1 text-xs px-3 py-2 bg-stone-100 text-stone-600 rounded-lg hover:bg-stone-200 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-close-line"></i> Temizle
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <select
                  value={restCityFilter}
                  onChange={(e) => setRestCityFilter(e.target.value)}
                  className="text-xs border border-stone-200 rounded-lg px-3 py-2 text-stone-600 outline-none focus:border-amber-400 bg-white cursor-pointer"
                >
                  <option value="all">Tüm Şehirler</option>
                  {restCities.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <select
                  value={restCuisineFilter}
                  onChange={(e) => setRestCuisineFilter(e.target.value)}
                  className="text-xs border border-stone-200 rounded-lg px-3 py-2 text-stone-600 outline-none focus:border-amber-400 bg-white cursor-pointer"
                >
                  <option value="all">Tüm Mutfaklar</option>
                  {restCuisines.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <select
                  value={restPriceFilter}
                  onChange={(e) => setRestPriceFilter(e.target.value)}
                  className="text-xs border border-stone-200 rounded-lg px-3 py-2 text-stone-600 outline-none focus:border-amber-400 bg-white cursor-pointer"
                >
                  <option value="all">Tüm Fiyatlar</option>
                  {restPrices.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
                <p className="text-xs text-stone-400 ml-auto">{filtered.length} sonuç</p>
              </div>
            </div>

            <div className="flex items-center gap-2 mb-4 flex-wrap">
              {(["all", "pending", "approved", "rejected"] as const).map((f) => {
                const cnt = f === "all" ? applications.length : applications.filter((a) => a.status === f).length;
                return (
                  <button
                    key={f}
                    onClick={() => { setFilter(f); setSelectedRestIds(new Set()); }}
                    className={`text-xs px-4 py-2 rounded-full font-medium transition-all cursor-pointer whitespace-nowrap
                      ${filter === f ? "bg-stone-900 text-white" : "bg-white border border-stone-200 text-stone-600 hover:border-stone-300"}`}
                  >
                    {f === "all" ? "Tümü" : f === "pending" ? "Bekleyenler" : f === "approved" ? "Onaylananlar" : "Reddedilenler"}
                    {cnt > 0 && (
                      <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${
                        filter === f ? "bg-white/20 text-white" : f === "pending" ? "bg-amber-100 text-amber-700" : f === "approved" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"
                      }`}>{cnt}</span>
                    )}
                  </button>
                );
              })}
            </div>

            {selectedRestIds.size > 0 && (
              <div className="flex items-center gap-3 mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl flex-wrap">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 flex items-center justify-center bg-amber-500 rounded-full">
                    <i className="ri-checkbox-multiple-line text-white text-xs"></i>
                  </div>
                  <span className="text-sm font-semibold text-amber-800">{selectedRestIds.size} başvuru seçildi</span>
                </div>
                <div className="flex items-center gap-2 ml-auto flex-wrap">
                  <button
                    disabled={restBulkProcessing}
                    onClick={() => {
                      setRestBulkProcessing(true);
                      const pending: PendingRestaurant[] = JSON.parse(localStorage.getItem("pendingRestaurants") || "[]");
                      const updated = pending.map((p) =>
                        selectedRestIds.has(p.id) && p.status === "pending" ? { ...p, status: "approved" as const } : p
                      );
                      const approved = JSON.parse(localStorage.getItem("approvedRestaurants") || "[]");
                      const newlyApproved = pending.filter((p) => selectedRestIds.has(p.id) && p.status === "pending");
                      newlyApproved.forEach((app) => {
                        approved.push({
                          id: app.restaurantName.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "") + "-" + Date.now(),
                          name: app.restaurantName,
                          city: app.city,
                          district: app.district,
                          cuisine: app.cuisine,
                          shortDesc: app.shortDesc,
                          description: app.description,
                          image: app.image,
                          images: app.images,
                          tags: app.tags,
                          rating: 4.5,
                          priceRange: app.priceRange,
                          mustTry: app.mustTry,
                          featured: false,
                          address: app.address,
                          phone: app.phone,
                          website: app.website,
                          instagram: app.instagram,
                          openingHours: app.openingHours,
                          ownerSubmitted: true,
                          approvedAt: new Date().toISOString(),
                        });
                      });
                      localStorage.setItem("pendingRestaurants", JSON.stringify(updated));
                      localStorage.setItem("approvedRestaurants", JSON.stringify(approved));
                      setApplications(updated);
                      setSelectedRestIds(new Set());
                      setRestBulkProcessing(false);
                      showToast(`${newlyApproved.length} restoran başvurusu toplu olarak onaylandı!`, "success");
                    }}
                    className="flex items-center gap-1.5 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white text-xs font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {restBulkProcessing ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-checkbox-circle-line"></i>}
                    Tümünü Onayla
                  </button>
                  <button
                    disabled={restBulkProcessing}
                    onClick={() => {
                      setRestBulkProcessing(true);
                      const pending: PendingRestaurant[] = JSON.parse(localStorage.getItem("pendingRestaurants") || "[]");
                      const updated = pending.map((p) =>
                        selectedRestIds.has(p.id) && p.status === "pending" ? { ...p, status: "rejected" as const } : p
                      );
                      localStorage.setItem("pendingRestaurants", JSON.stringify(updated));
                      setApplications(updated);
                      setSelectedRestIds(new Set());
                      setRestBulkProcessing(false);
                      showToast(`${selectedRestIds.size} restoran başvurusu reddedildi.`, "error");
                    }}
                    className="flex items-center gap-1.5 px-4 py-2 bg-rose-500 hover:bg-rose-600 disabled:opacity-50 text-white text-xs font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-close-circle-line"></i> Tümünü Reddet
                  </button>
                  <button
                    onClick={() => setSelectedRestIds(new Set())}
                    className="flex items-center gap-1.5 px-3 py-2 bg-stone-100 hover:bg-stone-200 text-stone-600 text-xs font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-close-line"></i> Seçimi Kaldır
                  </button>
                </div>
              </div>
            )}

            {filtered.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-2xl border border-stone-100">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-inbox-line text-stone-300 text-4xl"></i>
                </div>
                <p className="text-stone-400 text-sm">
                  {filter === "pending" ? "Bekleyen başvuru yok." : "Bu kategoride başvuru bulunamadı."}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                <div className="lg:col-span-1 space-y-2">
                  {filtered.filter((a) => a.status === "pending").length > 0 && (
                    <div className="flex items-center gap-2 px-3 py-2 bg-stone-50 rounded-xl border border-stone-100 mb-1">
                      <button
                        onClick={() => {
                          const pendingIds = filtered.filter((a) => a.status === "pending").map((a) => a.id);
                          const allSelected = pendingIds.every((id) => selectedRestIds.has(id));
                          if (allSelected) {
                            setSelectedRestIds(new Set());
                          } else {
                            setSelectedRestIds(new Set(pendingIds));
                          }
                        }}
                        className={`w-4 h-4 flex items-center justify-center rounded border-2 flex-shrink-0 transition-all cursor-pointer ${
                          filtered.filter((a) => a.status === "pending").every((a) => selectedRestIds.has(a.id))
                            ? "bg-amber-500 border-amber-500"
                            : "border-stone-300 hover:border-amber-400"
                        }`}
                      >
                        {filtered.filter((a) => a.status === "pending").every((a) => selectedRestIds.has(a.id)) && (
                          <i className="ri-check-line text-white text-xs"></i>
                        )}
                      </button>
                      <span className="text-xs text-stone-500 font-medium">
                        Bekleyen {filtered.filter((a) => a.status === "pending").length} başvuruyu seç
                      </span>
                    </div>
                  )}

                  {filtered.map((app) => {
                    const isChecked = selectedRestIds.has(app.id);
                    return (
                      <div
                        key={app.id}
                        className={`relative w-full text-left p-4 rounded-xl border transition-all ${
                          selectedApp?.id === app.id ? "border-amber-400 bg-amber-50" : isChecked ? "border-amber-300 bg-amber-50/50" : "border-stone-100 bg-white hover:border-amber-200"
                        }`}
                      >
                        {app.status === "pending" && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedRestIds((prev) => {
                                const next = new Set(prev);
                                if (next.has(app.id)) next.delete(app.id);
                                else next.add(app.id);
                                return next;
                              });
                            }}
                            className={`absolute top-3 left-3 w-4 h-4 flex items-center justify-center rounded border-2 flex-shrink-0 transition-all cursor-pointer z-10 ${
                              isChecked ? "bg-amber-500 border-amber-500" : "border-stone-300 bg-white hover:border-amber-400"
                            }`}
                          >
                            {isChecked && <i className="ri-check-line text-white text-xs"></i>}
                          </button>
                        )}
                        <button
                          onClick={() => {
                            setSelectedApp(app);
                            const merged = Array.from(new Set([...seenIds, app.id]));
                            setSeenIds(merged);
                            localStorage.setItem("adminSeenIds", JSON.stringify(merged));
                          }}
                          className={`w-full text-left ${app.status === "pending" ? "pl-6" : ""}`}
                        >
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="flex items-center gap-2">
                              {!seenIds.includes(app.id) && app.status === "pending" && (
                                <span className="w-2 h-2 rounded-full bg-amber-500 flex-shrink-0 mt-0.5"></span>
                              )}
                              <h3 className="font-semibold text-stone-900 text-sm" style={{ fontFamily: "'Playfair Display', serif" }}>
                                {app.restaurantName}
                              </h3>
                            </div>
                            {statusBadge(app.status)}
                          </div>
                          <div className="flex items-center gap-1 mb-1">
                            <i className="ri-map-pin-line text-amber-500 text-xs"></i>
                            <span className="text-xs text-stone-500">{app.city}, {app.district}</span>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs text-stone-400">{app.cuisine}</span>
                            <span className="text-xs font-bold text-stone-600">{app.priceRange}</span>
                          </div>
                          <div className="flex items-center justify-between mt-2">
                            <p className="text-xs text-stone-400">
                              {new Date(app.appliedAt).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })}
                            </p>
                            <button
                              onClick={(e) => { e.stopPropagation(); navigate(`/admin/application/${app.id}`); }}
                              className="text-xs text-amber-600 hover:text-amber-700 flex items-center gap-0.5 cursor-pointer whitespace-nowrap"
                            >
                              Detay <i className="ri-external-link-line text-xs"></i>
                            </button>
                          </div>
                        </button>
                      </div>
                    );
                  })}
                </div>

                <div className="lg:col-span-2">
                  {selectedApp ? (
                    <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden">
                      <div className="relative h-48 overflow-hidden">
                        <img src={selectedApp.image} alt={selectedApp.restaurantName} className="w-full h-full object-cover object-top" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                        <div className="absolute bottom-4 left-4 right-4">
                          <div className="flex items-end justify-between">
                            <div>
                              <h2 className="text-xl font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                                {selectedApp.restaurantName}
                              </h2>
                              <p className="text-stone-300 text-xs">{selectedApp.city}, {selectedApp.district}</p>
                            </div>
                            {statusBadge(selectedApp.status)}
                          </div>
                        </div>
                      </div>

                      <div className="p-6">
                        <div className="grid grid-cols-2 gap-3 mb-5">
                          <div className="bg-stone-50 rounded-lg p-3">
                            <p className="text-xs text-stone-400 mb-0.5">İşletme Sahibi</p>
                            <p className="text-sm font-semibold text-stone-800">{selectedApp.ownerName}</p>
                          </div>
                          <div className="bg-stone-50 rounded-lg p-3">
                            <p className="text-xs text-stone-400 mb-0.5">E-posta</p>
                            <p className="text-sm font-semibold text-stone-800 truncate">{selectedApp.email}</p>
                          </div>
                          <div className="bg-stone-50 rounded-lg p-3">
                            <p className="text-xs text-stone-400 mb-0.5">Telefon</p>
                            <p className="text-sm font-semibold text-stone-800">{selectedApp.phone}</p>
                          </div>
                          <div className="bg-stone-50 rounded-lg p-3">
                            <p className="text-xs text-stone-400 mb-0.5">Mutfak &amp; Fiyat</p>
                            <p className="text-sm font-semibold text-stone-800">{selectedApp.cuisine} · {selectedApp.priceRange}</p>
                          </div>
                        </div>

                        <div className="mb-4">
                          <p className="text-xs text-stone-400 mb-1">Adres</p>
                          <p className="text-sm text-stone-700">{selectedApp.address}</p>
                        </div>
                        <div className="mb-4">
                          <p className="text-xs text-stone-400 mb-1">Kısa Açıklama</p>
                          <p className="text-sm text-stone-700">{selectedApp.shortDesc}</p>
                        </div>
                        <div className="mb-5">
                          <p className="text-xs text-stone-400 mb-2">Mutlaka Denenecekler</p>
                          <div className="flex flex-wrap gap-1.5">
                            {(Array.isArray(selectedApp.mustTry) ? selectedApp.mustTry : [selectedApp.mustTry]).map((item, i) => (
                              <span key={i} className="bg-amber-50 text-amber-700 text-xs px-2 py-0.5 rounded-full">{item}</span>
                            ))}
                          </div>
                        </div>

                        {/* Admin Notu */}
                        <div className="mb-4 p-4 bg-stone-50 rounded-xl border border-stone-100">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className="w-5 h-5 flex items-center justify-center">
                                <i className="ri-sticky-note-line text-stone-500 text-sm"></i>
                              </div>
                              <p className="text-xs font-semibold text-stone-700">Admin Notu</p>
                            </div>
                            <button
                              onClick={() => { setShowRestNoteEdit((v) => !v); setRestNoteEdit(selectedApp.adminNote || ""); }}
                              className="text-xs text-amber-600 hover:text-amber-700 cursor-pointer whitespace-nowrap"
                            >
                              {showRestNoteEdit ? "İptal" : "Düzenle"}
                            </button>
                          </div>
                          {showRestNoteEdit ? (
                            <div>
                              <textarea
                                value={restNoteEdit}
                                onChange={(e) => setRestNoteEdit(e.target.value)}
                                rows={3}
                                maxLength={300}
                                placeholder="Bu başvuru hakkında iç not ekleyin..."
                                className="w-full border border-stone-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors resize-none bg-white"
                              />
                              <div className="flex items-center justify-between mt-1.5">
                                <p className="text-xs text-stone-400">{restNoteEdit.length}/300</p>
                                <button
                                  onClick={() => {
                                    const all = JSON.parse(localStorage.getItem("pendingRestaurants") || "[]");
                                    const updated = all.map((a: PendingRestaurant) => a.id === selectedApp.id ? { ...a, adminNote: restNoteEdit } : a);
                                    localStorage.setItem("pendingRestaurants", JSON.stringify(updated));
                                    setApplications(updated);
                                    setSelectedApp((prev) => prev ? { ...prev, adminNote: restNoteEdit } : prev);
                                    setShowRestNoteEdit(false);
                                    showToast("Not kaydedildi.", "success");
                                  }}
                                  className="px-4 py-1.5 bg-stone-900 text-white text-xs font-semibold rounded-lg cursor-pointer whitespace-nowrap hover:bg-stone-700 transition-colors"
                                >
                                  Kaydet
                                </button>
                              </div>
                            </div>
                          ) : (
                            <p className="text-sm text-stone-500 min-h-[40px]">
                              {selectedApp.adminNote || <span className="text-stone-300 italic text-xs">Not eklenmemiş — Düzenle&apos;ye tıklayın</span>}
                            </p>
                          )}
                        </div>

                        <div className="mb-5 p-4 bg-stone-50 rounded-xl border border-stone-100">
                          <div className="flex items-center gap-2 mb-3">
                            <div className="w-5 h-5 flex items-center justify-center">
                              <i className="ri-mail-line text-stone-600 text-sm"></i>
                            </div>
                            <p className="text-xs font-semibold text-stone-700">E-posta Bildirimi</p>
                            {selectedApp.notified && (
                              <span className="bg-emerald-100 text-emerald-700 text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                                <i className="ri-check-line text-xs"></i> Bildirim Gönderildi
                              </span>
                            )}
                          </div>
                          <div className="flex gap-2 flex-wrap">
                            <button
                              onClick={() => sendEmailNotification(selectedApp, "approve")}
                              disabled={sendingEmail !== null}
                              className="flex items-center gap-1.5 text-xs px-3 py-2 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                            >
                              {sendingEmail === "approve" ? <><i className="ri-loader-4-line animate-spin"></i> Hazırlanıyor...</> : <><i className="ri-mail-check-line"></i> Onay E-postası Gönder</>}
                            </button>
                            <button
                              onClick={() => sendEmailNotification(selectedApp, "reject")}
                              disabled={sendingEmail !== null}
                              className="flex items-center gap-1.5 text-xs px-3 py-2 bg-rose-50 text-rose-700 border border-rose-200 rounded-lg hover:bg-rose-100 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                            >
                              {sendingEmail === "reject" ? <><i className="ri-loader-4-line animate-spin"></i> Hazırlanıyor...</> : <><i className="ri-mail-close-line"></i> Red E-postası Gönder</>}
                            </button>
                          </div>
                        </div>

                        {selectedApp.status === "pending" && (
                          <div className="flex gap-3 pt-4 border-t border-stone-100">
                            <button
                              onClick={() => handleApprove(selectedApp)}
                              className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-emerald-500 text-white rounded-xl text-sm font-medium hover:bg-emerald-600 transition-colors cursor-pointer whitespace-nowrap"
                            >
                              <i className="ri-checkbox-circle-line"></i> Onayla &amp; Yayınla
                            </button>
                            <button
                              onClick={() => handleReject(selectedApp)}
                              className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-rose-500 text-white rounded-xl text-sm font-medium hover:bg-rose-600 transition-colors cursor-pointer whitespace-nowrap"
                            >
                              <i className="ri-close-circle-line"></i> Reddet
                            </button>
                          </div>
                        )}

                        {selectedApp.status !== "pending" && (
                          <div className="flex gap-3 pt-4 border-t border-stone-100">
                            <button
                              onClick={() => handleDelete(selectedApp.id)}
                              className="flex items-center gap-2 px-4 py-2.5 border border-rose-200 text-rose-600 rounded-xl text-sm font-medium hover:bg-rose-50 transition-colors cursor-pointer whitespace-nowrap"
                            >
                              <i className="ri-delete-bin-line"></i> Sil
                            </button>
                            {selectedApp.status === "rejected" && (
                              <button
                                onClick={() => handleApprove(selectedApp)}
                                className="flex items-center gap-2 px-4 py-2.5 bg-emerald-500 text-white rounded-xl text-sm font-medium hover:bg-emerald-600 transition-colors cursor-pointer whitespace-nowrap"
                              >
                                <i className="ri-checkbox-circle-line"></i> Yine de Onayla
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="bg-white rounded-2xl border border-stone-100 h-64 flex flex-col items-center justify-center">
                      <div className="w-12 h-12 flex items-center justify-center mb-3">
                        <i className="ri-file-list-3-line text-stone-300 text-3xl"></i>
                      </div>
                      <p className="text-stone-400 text-sm">Detayları görmek için bir başvuru seçin</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default AdminPage;
